/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {alpha, createTheme, Theme, ThemeOptions} from '@material-ui/core/styles';
import isNumber from 'lodash.isnumber';
import isObject from 'lodash.isobject';
import merge from 'lodash.merge';
import mergeWith from 'lodash.mergewith';
import {warn} from '@ion/core/src/logger/logger';
import {GenericObject} from '@ion/core/src/types/entities';
import {deepCopy} from '@ion/core/src/utils/ObjectUtil';
import {
  DLSCurrentFallbackSection,
  DLSShadowColorOverride,
  DLSShadowStyle,
  DLSTheme,
  Elevation,
  ELEVATION_TO_SHADOW_MAP,
  IconSize,
  OverlayName,
  TextColorName
} from '../types/types';
// @ts-ignore
import currentTheme from './current/theme.json5';
// @ts-ignore
import currentDarkDelta from './current/theme-dark.json5';
// @ts-ignore
import legacyTheme from './legacy/theme.json5';
import {logThemeResolutionError} from './DLSThemeLogger';
import loadExternalTheme from './ThemeLoader';
import {
  addEffectiveThemeModeObserver,
  addMultiThemeEnabledObserver,
  addThemeChangeObserver,
  currentTheme as currentThemeSignal,
  isDarkMode,
  multiThemeEnabled
} from './ThemeStore';
import {DLSFontWeight, DLSThemes, LegacyDLSFontWeight} from './types';

/**
 * The theme module defines the Material UI theme used by the application,
 * and exports all theme styles which are based on the iOn Suite UX / UI
 * Design Language System (iOn DLS). Please refer to the documentation below
 * for details:
 * https://www.figma.com/file/srjlHrDuM90sCoaOwpjaDU/02_DLS_Components?node-id=4%3A2
 *
 * Material UI based theme styles are available via their respective theme
 * names. For a complete list, please refer to the Material UI docs default
 * theme below:
 * https://github.com/callemall/material-ui/blob/master/src/styles/getMuiTheme.js
 *
 * Likewise, application specific styles which are not exposed as Material
 * UI theme names are provided via  the "custom" property of the theme.json
 * document, and are exported via the theme module as well.
 *
 * This theme module simply exports themes styles, effectively making them
 * available to the Application container as well as to all components within
 * the application. The actual theme styles and definitions are defined with
 * the corresponding theme.json document. The JSON document affords the ability
 * to use the same theme in both JavaScript as well as within SASS.
 *
 * As mentioned, all styles are defined within the corresponding theme.json
 * document, and are added to the exported Material UI theme via this module.
 * As such, Presentational Components can be wrapped in muiThemeable and
 * subsequently consume styles explicitly via props.theme
 *
 * ```typescript
 *   import {dlsLayout} from '@ion/dls/src/theme/theme';
 *   const {mobile, desktop} = dlsLayout('breakpoints'); // 414, 1440
 *   // or ...
 *   const breakpoint = dlsLayout('breakpoints', 'mobile'); // 414, 1440
 *
 * ```typescript
 *   import {dlsColor} from '@ion/dls/src/theme/theme';
 *   const color = dlsColor('caBlue'); // '#02ABCA'
 *
 * ```
 *
 * ```typescript
 *   import {dlsTypography} from '@ion/dls/src/theme/theme';
 *   const {fontFamily, fontWeight, fontSize, lineHeight} = dlsTypography('h1');
 *
 * ```
 *
 * ```typescript
 *   import React from 'react';
 *   import {withTheme} from '@material-ui/core/styles';
 *
 *   const ExampleComponent = ({theme}) => (
 *     // reference default.json "custom.primaryColor" style ...
 *     <span style={{color: theme.palette.primary}}>
 *        Example!
 *     </span>
 *   );
 *   export default withTheme(ExampleComponent);
 *
 * ```
 *
 * @module
 *
 */

/**
 * @deprecated
 *
 * Note: The `dls` and `palette` objects are deprecated and only provided for backwards
 * compatibility and should never be used in new implementations. These values are not
 * guaranteed to reflect the currently selected DLS theme, and the dedicated theme
 * helper functions, such as `dlsColor`, `dlsLayout`, etc. should always be used
 * instead.
 *
 *
 * Provides the top-level exports of the theme, the dls object, which encompasses
 * the entire iOn Suite Design Language System, and the palette object, which
 * encompasses all Material UI theming definitions.
 */
export let dls, palette;

/**
 * Stores the currently active custom theme, if one has been created.
 */
let customTheme: DLSTheme | null = null;

/**
 * Caches the last resolved theme to avoid repeated references or reassignments.
 */
let cachedTheme: DLSTheme;

/**
 * Tracks which DLS theme the active theme instance is currently based on.
 *
 * This internal reference is primarily needed when a custom theme has been
 * created via `createCustomTheme`, as the theme store alone does not track
 * the state of whether or not a custom theme is being used from the legacy
 * or current DLS theme.
 *
 * Helpers such as `dlsFontWeight`, which resolve between argument values as
 * opposed to referencing `theme.json` entries directly, use this value to
 * ensure they are aligned with the effective base theme of the active custom
 * theme.
 *
 * When a custom theme is not active, this value is kept in sync with the
 * currently selected theme via the internal `updateCachedTheme` function.
 */
let activeBaseTheme: DLSThemes = DLSThemes.legacy;

/**
 * Provides an internal utility which resolves the currently selected DLS
 * theme from the theme store.
 *
 * The current theme is returned only when multi-theme support is enabled and
 * the selected theme in the store is `DLSThemes.current`. In all other cases,
 * the legacy theme is treated as active. This mirrors the existing theme
 * selection behavior used by other `theme.ts` APIs when resolving the
 * selected theme.
 *
 * Note that this helper does not account for custom themes created via
 * `createCustomTheme`; those rely on `activeBaseTheme` instead.
 */
const getSelectedTheme = (): DLSThemes => (
  multiThemeEnabled.value && currentThemeSignal?.value === DLSThemes.current
    ? DLSThemes.current
    : DLSThemes.legacy
);

/**
 * @private
 *
 * Replaces an array entry whole when merging the dark delta document, as the
 * SCSS `map.deep-merge` in `theme-dark-vars.scss` does; all other entries are
 * merged by the default `mergeWith` behavior.
 */
const replaceArrays = (_: unknown, source: unknown) => (
  Array.isArray(source) ? deepCopy(source) : undefined
);

/**
 * Builds a dark theme document by merging the given dark delta document over a
 * deep copy of the given light document. Entries of the delta replace those of
 * the light document, arrays are replaced whole, and every entry the delta does
 * not define is inherited from the light document, so an entry added to the
 * light document later is also present in the dark document. Neither argument
 * is modified.
 *
 * ```typescript
 *   import {buildDarkTheme} from '@ion/dls/src/theme/theme';
 *   ...
 *
 *   // every entry of the light document other than white is inherited ...
 *   const theme = buildDarkTheme(lightTheme, {dls: {colors: {white: '#19282D'}}});
 * ```
 *
 * @param light The light theme document which provides every entry.
 * @param delta The dark entries which differ from the light document.
 * @returns     The merged dark theme document.
 */
export const buildDarkTheme = (light: DLSTheme, delta: GenericObject): DLSTheme => (
  mergeWith(deepCopy(light), delta, replaceArrays)
);

/**
 * @private
 *
 * Indicates if the dark delta document provides both the `dls` and `palette`
 * entries, which the dark theme requires to be resolved.
 */
const isDarkDeltaValid = Boolean(currentDarkDelta?.dls && currentDarkDelta?.palette);

/**
 * The current dark theme document: the dark delta document
 * (`current/theme-dark.json5`) merged over the current (light) document via
 * `buildDarkTheme`. Built once, when this module loads. Implementations should
 * use the theme helpers, such as `dlsColor`, which resolve this document while
 * dark mode is active, rather than reading it directly.
 *
 * ```typescript
 *   import {darkTheme} from '@ion/dls/src/theme/theme';
 *   ...
 *
 *   // the dark value of the white surface ...
 *   const white = darkTheme.dls.colors.white; // '#19282D'
 * ```
 */
export const darkTheme: DLSTheme = buildDarkTheme(currentTheme, currentDarkDelta);

/**
 * Returns the Material UI palette of the current dark theme, or `undefined` when
 * the dark delta document is missing the `dls` or `palette` entry, in which case
 * the dark theme is never resolved and callers fall back to the light theme.
 *
 * ```typescript
 *   import {getCurrentDarkPalette} from '@ion/dls/src/theme/theme';
 *   ...
 *
 *   // creates the dark Material UI theme when a dark palette is provided ...
 *   const palette = getCurrentDarkPalette();
 *   const theme   = palette && createTheme({palette});
 * ```
 *
 * @returns The dark palette, or `undefined` when the dark document is invalid.
 */
export const getCurrentDarkPalette = (): ThemeOptions['palette'] | undefined => (
  isDarkDeltaValid ? darkTheme.palette : undefined
);

/**
 * Resolves the current theme document for the effective theme mode. The dark
 * document is used only when the effective mode is dark and the dark delta
 * document provides both the `dls` and `palette` entries; otherwise the error
 * is logged and the current (light) document is used instead.
 */
const resolveCurrentTheme = (): DLSTheme => {
  let resolvedTheme = currentTheme;
  if (isDarkMode()) {
    if (isDarkDeltaValid) {
      resolvedTheme = darkTheme;
    } else {
      logThemeResolutionError(
        DLSThemes.current,
        new Error('The dark theme document is missing the dls or palette entry')
      );
    }
  }
  return resolvedTheme;
};

/**
 * Updates the cached theme based on selected theme state, enablement, and the
 * effective theme mode. A custom theme is never replaced, so a custom theme
 * never resolves dark. `useExternalTheme` also turns the dark opt-in off.
 */
const updateCachedTheme = (): void => {
  if (!customTheme) {
    activeBaseTheme = getSelectedTheme();
    const resolvedTheme = activeBaseTheme === DLSThemes.current ? resolveCurrentTheme() : legacyTheme;
    cachedTheme = resolvedTheme;
    dls     = resolvedTheme.dls;
    palette = resolvedTheme.palette;
  }
};
updateCachedTheme();

// we subscribe to changes of the selected theme and update the cached them to
// reflect the currently selected theme ...
addThemeChangeObserver(updateCachedTheme);
addMultiThemeEnabledObserver(updateCachedTheme);
addEffectiveThemeModeObserver(updateCachedTheme);

/**
 * Returns the `theme.json5` document based on the currently selected theme. Note,
 * when invoked, the exported `dls` and `palette` objects will also be updated to
 * reference the respective properties from the currently selected theme, unless a
 * custom theme is currently active.
 */
const getThemeConfig = (): DLSTheme => (
  customTheme || cachedTheme
);

/**
 * Returns the current (light) `theme.json5` document while the current dark
 * document is the resolved theme, otherwise the same document returned by
 * `getThemeConfig`. Used by the light-pinned helpers, such as `dlsLightColor`,
 * for graphics which always render light (e.g. map-drawn graphics).
 */
const getLightThemeConfig = (): DLSTheme => (
  !customTheme && cachedTheme === darkTheme ? currentTheme : getThemeConfig()
);

/**
 * Reads a token value from a DLS section on the provided theme.
 *
 * This is intentionally untyped at the token level because the helper is
 * shared by multiple token sections with different key unions.
 */
const getDlsTokenValue = (
  theme: DLSTheme,
  section: DLSCurrentFallbackSection,
  token: string
) => ((theme.dls as GenericObject)[section] as GenericObject | undefined)?.[token];

/**
 * Resolves a DLS token from the active theme and falls back to current theme
 * when the active theme does not provide that token section (legacy/custom).
 */
const getDlsTokenWithCurrentFallback = (
  section: DLSCurrentFallbackSection,
  token: string
) => getDlsTokenValue(getThemeConfig(), section, token) ??
  getDlsTokenValue(currentTheme as DLSTheme, section, token);

/**
 * Returns the given Color for the DLS color entry, see the link below for all
 * valid values.
 *
 * ```typescript
 *   const color = dlsColor('caBlue');   // '#02ABCA'
 *   const color = dlsColor('darkTeal'); // '#0C6178'
 *
 * ```
 *
 * @method dlsColor
 * @see https://www.figma.com/file/gtF4hB3eC4fvzKVJ2DNDtu/01_DLS_Foundation?node-id=1%3A167
 *
 */
export const dlsColor = (color: string) => getColor(getThemeConfig(), color);

/**
 * Returns the given Color for the DLS color entry from the current (light)
 * theme while dark mode is active, otherwise the same value as `dlsColor`.
 * Use it only for graphics which always render light, such as map-drawn
 * tiles, markers, layers, geofences, masks, and labels.
 *
 * ```typescript
 *   import {dlsLightColor} from '@ion/dls/src/theme/theme';
 *   ...
 *
 *   // resolves the light value in both light and dark modes ...
 *   const color = dlsLightColor('white'); // '#FFFFFF'
 *
 * ```
 *
 * @method dlsLightColor
 */
export const dlsLightColor = (color: string) => getColor(getLightThemeConfig(), color);

/**
 * @private
 *
 * Reads the given color entry from the provided theme and logs a warning when
 * the entry is not defined.
 */
const getColor = (theme: DLSTheme, color: string) => {
  const val = theme.dls.colors[color];
  if (!val) {
    logger('color', color);
  }
  return val;
};

/**
 * Returns the given Layout category for the DLS layout entry, see the link below
 * for all valid values.
 *
 * ```typescript
 *   const {mobile, desktop} = dlsLayout('breakpoints'); // 414, 1440
 *   const {mobile, desktop} = dlsLayout('gutters'); // 16, 24
 *   const columns = dlsLayout('columns'); // 12
 *   const margins = dlsLayout('margins'); // 24
 *   const spacing = dlsLayout('spacing'); // 8
 *
 * ```
 *
 * @method dlsLayout
 * @see https://www.figma.com/file/gtF4hB3eC4fvzKVJ2DNDtu/01_DLS_Foundation?node-id=207%3A6
 *
 */
export const dlsLayout = (...args: string[]) => {
  const theme = getThemeConfig();
  const val   = getEntry(theme.dls.layout, ...args);
  if (!val) {
    logger('layout', ...args);
  }
  return val;
};

/**
 * Returns the shadow token value for the given DLS elevation level.
 * Only predefined elevation values are supported.
 *
 * ```typescript
 *   const shadowLow      = dlsShadow(1);
 *   const shadowMedium   = dlsShadow(3);
 *   const shadowHigh     = dlsShadow(5.2);
 *   const dropShadow     = dlsShadow(6, DLSShadowStyle.DropShadow);
 *   const insetShadow    = dlsShadow(6.3, DLSShadowStyle.Inset);
 *   const stackedShadows = dlsShadow(6.2, 6.3);
 *   const recoloredInset = dlsShadow(7.2, DLSShadowStyle.Inset, {color: '#0097AF'});
 * ```
 *
 * @param elevation - DLS elevation level (e.g. 1, 1.2, 3, 5.2)
 * @param args - Optional modifiers and additional elevations:
 * - `DLSShadowStyle.Raw` (default): raw shadow output for `box-shadow`
 * - `DLSShadowStyle.DropShadow`: wraps output as `drop-shadow(<token>)` for CSS `filter`
 * - `DLSShadowStyle.Inset`: prefixes raw shadow output with `inset`
 * - `{color: string}`: replaces the token's color value while preserving geometry
 * - Additional `Elevation` numbers are joined as comma-separated shadows
 * @returns The resolved shadow value from the theme
 */
export const dlsShadow = (
  elevation: Elevation,
  ...args: Array<Elevation | DLSShadowStyle | DLSShadowColorOverride>
) => {
  let output: DLSShadowStyle.Raw | DLSShadowStyle.DropShadow = DLSShadowStyle.Raw;
  let inset                         = false;
  let colorOverride: string | null  = null;
  const elevations                  = [elevation] as Elevation[];
  const colorPattern                = /(#[\da-fA-F]{3,8}|rgba?\([^)]+\)|hsla?\([^)]+\))(?=\s*$)/;

  args.forEach((arg) => {
    if (arg === DLSShadowStyle.Raw || arg === DLSShadowStyle.DropShadow) {
      output = arg;
      return;
    }
    if (arg === DLSShadowStyle.Inset) {
      inset = true;
      return;
    }
    if (isObject(arg) && 'color' in arg && arg.color) {
      colorOverride = arg.color;
      return;
    }
    if (isNumber(arg)) {
      elevations.push(arg as Elevation);
    }
  });

  const shadows = elevations
    .map((currentElevation) => {
      const shadowKey = ELEVATION_TO_SHADOW_MAP[currentElevation];
      const val       = getDlsTokenWithCurrentFallback('shadows', shadowKey);

      if (!val) {
        logger('shadow', currentElevation.toString());
        return null;
      }
      const resolvedValue = colorOverride ? val.replace(colorPattern, colorOverride) : val;

      if (output === DLSShadowStyle.DropShadow) {
        return `drop-shadow(${resolvedValue})`;
      }

      return inset ? `inset ${resolvedValue}` : resolvedValue;
    })
    .filter(Boolean);

  return shadows.join(', ');
};

/**
 * Returns the overlay color value for the given DLS overlay name.
 * Only valid overlay tokens defined in the design system are allowed.
 *
 * ```typescript
 *   const blackOverlay   = dlsOverlay('black');
 *   const skyBlueOverlay = dlsOverlay('skyBlue');
 * ```
 *
 * @param name - DLS overlay name
 * @returns The resolved overlay color value from the theme
 */
export const dlsOverlay = (name: OverlayName) => {
  const val = getDlsTokenWithCurrentFallback('overlays', name);
  if (!val) {
    logger('overlay', name);
  }
  return val;
};

/**
 * Returns the text color value for the given DLS text color token.
 * Only predefined text color names are supported.
 *
 * ```typescript
 *   const primaryText   = dlsTextColor('red');
 *   const secondaryText = dlsTextColor('orange');
 *   const disabledText  = dlsTextColor('yellow');
 * ```
 *
 * @param color - DLS text color name
 * @returns The resolved text color value from the theme
 */
export const dlsTextColor = (color: TextColorName) => {
  const val = getDlsTokenWithCurrentFallback('textColors', color);
  if (!val) {
    logger('text color', color);
  }
  return val;
};

/**
 * Returns the given Typography category for the DLS typography entry, see the
 * link below for all valid values.
 *
 * ```typescript
 *   const {fontFamily, fontWeight, fontSize, lineHeight} = dlsTypography('h1');
 *
 * ```
 *
 * @see https://www.figma.com/file/gtF4hB3eC4fvzKVJ2DNDtu/01_DLS_Foundation?node-id=1244%3A34
 */
export const dlsTypography = (...args: string[]) => {
  const theme = getThemeConfig();
  const val   = getEntry(theme.dls.typography, ...args);
  if (!val) {
    logger('typography', ...args);
  }
  return val;
};

/**
 * Provides a helper which returns the provided font weight for the currently
 * active DLS theme.
 *
 * When a custom theme is not active, this function will follow the same store
 * driven theme selection used by all `theme.ts` APIs.
 *
 * When a custom theme is active, it will resolve against the base theme used
 * to create that custom theme via `createCustomTheme(..., latest)`, ensuring
 * the result is consistent with the active theme instance.
 *
 * ```typescript
 *   const fontWeight = dlsFontWeight(400, 300); // 400 for current, 300 for legacy ...
 *   ...
 *
 *   const useStyles = makeStyles(() => ({
 *     title: {
 *       ...dlsTypography('body3'),
 *       fontSize   : '16px',
 *       fontWeight : dlsFontWeight(600, 500)
 *     }
 *   }));
 * ```
 *
 * @param   current The font weight to use when the current DLS theme is active.
 *          Supported values: `400 | 500 | 600 | 700`.
 * @param   legacy The font weight to use when the legacy DLS theme is active.
 *          Supported values: `300 | 400 | 500 | 600 | 700`.
 * @returns The resolved DLS font weight for the active theme.
 */
export const dlsFontWeight = (
  current: DLSFontWeight,
  legacy: LegacyDLSFontWeight
): LegacyDLSFontWeight => {
  const selectedTheme = customTheme ? activeBaseTheme : getSelectedTheme();
  if (selectedTheme === DLSThemes.current) {
    return current;
  }
  return legacy;
};

/**
 * Returns the icon color based on the provided icon size for the DLS icon entry,
 * see the link below for valid values.
 *
 * ```typescript
 *   const color = dlsIconColor(IconSize.large);  // grey3 (see dls.colors.grey3)
 *   const color = dlsIconColor(IconSize.small);  // grey2 (see dls.colors.grey2)
 *   const color = dlsIconColor(IconSize.medium); // grey2 (see dls.colors.grey2)
 *
 * ```
 *
 * @see https://www.figma.com/file/gtF4hB3eC4fvzKVJ2DNDtu/00_DLS_Foundation?node-id=1%3A172
 *
 */
export const dlsIconColor = (size?: IconSize) => (
  dlsColor(size === IconSize.large ? 'grey4' : 'grey3')
);

/**
 *  Returns the zindex for corresponding component names.
 *
 * ```typescript
 *   const color = dlsZIndex('assetHistoryTitle');
 *   const color = dlsZIndex('navigationDiv');
 *
 * ```
 */
export const dlsZIndex = (zindex: string) => {
  const theme = getThemeConfig();
  return theme.dls.zIndices[zindex] || theme.dls.zIndices.defaultZIndex;
};

/**
 * Returns a value from the `shape` section of the currently selected
 * theme (e.g. the DLS corner-radius token).
 *
 * ```typescript
 *   const radius = dlsShape('borderRadius');
 *
 * ```
 */
export const dlsShape = (key: string) => {
  const theme = getThemeConfig();
  return theme.shape?.[key];
};

/**
 * Returns the given non-standard DLS definitions as defined within the
 * alt section of the dls entry in theme.json5.
 *
 * The alt section defines all alternative DLS specifications which are
 * non-standard, as, in some cases, specific components and or features
 * within the DLS may define colors, sizes, typography, etc. which does
 * not strictly conform to the general DLS specifications. In all cases,
 * such non-standard definitions are to be provided via the alt section
 * of the dls entry.
 *
 * ```typescript
 *   const {disabled} = dlsAlt('btn');
 *
 * ```
 */
export const dlsAlt = (...args: string[]) => getAlt(getThemeConfig(), ...args);

/**
 * Returns the given alternative DLS entry from the current (light) theme while
 * dark mode is active, otherwise the same value as `dlsAlt`. Use it only for
 * graphics which always render light, such as map-drawn layers.
 *
 * ```typescript
 *   import {dlsLightAlt} from '@ion/dls/src/theme/theme';
 *   ...
 *
 *   // resolves the light value in both light and dark modes ...
 *   const {color} = dlsLightAlt('maps', 'layer', 'listHeader'); // '#828282'
 *
 * ```
 */
export const dlsLightAlt = (...args: string[]) => getAlt(getLightThemeConfig(), ...args);

/**
 * @private
 *
 * Reads the given alternative entry from the provided theme and logs a warning
 * when the entry is not defined.
 */
const getAlt = (theme: DLSTheme, ...args: string[]) => {
  const val = getEntry(theme.dls.alt, ...args);
  if (!val) {
    logger('alternative', ...args);
  }
  return val;
};

/**
 *
 * Provides a utility which allows for conditionally returning one of two DLS
 * colors.
 *
 * ```typescript
 *   const color = dlsColorCondition(isAsset, 'caBlue' : 'caGreen');
 * ```
 */
export const dlsColorCondition = (condition: boolean, colorTrue: string, colorFalse: string): string => (
  dlsColor(condition ? colorTrue : colorFalse)
);

/**
 * Returns the DLS color of the given token while dark mode is active, otherwise
 * the given light value. Use it where a hard-coded light color is kept, such as
 * a color that a reseller theme would otherwise change, and only dark mode takes
 * the DLS token.
 *
 * ```typescript
 *   import {dlsDarkColor} from '@ion/dls/src/theme/theme';
 *   ...
 *
 *   // the dark grey5 token in dark mode, otherwise '#E0E0E0' ...
 *   const border = dlsDarkColor(isDark, 'grey5', '#E0E0E0');
 * ```
 *
 * @param isDark     `true` when dark mode is active.
 * @param token      The DLS color entry which is used in dark mode.
 * @param lightValue The color which is used in light mode.
 * @returns          The dark DLS color when `isDark`, otherwise `lightValue`.
 */
export const dlsDarkColor = (isDark: boolean, token: string, lightValue: string): string => (
  isDark ? dlsColor(token) : lightValue
);

/**
 * Returns the DLS color of the given token at the given alpha while dark mode
 * is active, otherwise the given light value untouched. Use it for a
 * translucent veil or border whose hard-coded light color must stay exactly as
 * it is, where only dark mode takes the DLS token.
 *
 * ```typescript
 *   import {dlsDarkAlpha} from '@ion/dls/src/theme/theme';
 *   ...
 *
 *   // the dark white token at 0x80 out of 0xFF, otherwise the light veil ...
 *   const veil = dlsDarkAlpha(isDark, 'white', {
 *     alphaValue : 0x80 / 255,
 *     lightValue : 'rgba(255, 255, 255, 0.5)'
 *   });
 * ```
 *
 * @param isDark  `true` when dark mode is active.
 * @param token   The DLS color entry which is used in dark mode.
 * @param options The `alphaValue` of the dark color, from 0 to 1, and the
 *                `lightValue` color which is used as is in light mode.
 * @returns       The dark DLS color at `alphaValue` when `isDark`, otherwise
 *                `lightValue`.
 */
export const dlsDarkAlpha = (
  isDark: boolean,
  token: string,
  {alphaValue, lightValue}: {alphaValue: number; lightValue: string}
): string => (
  isDark ? alpha(dlsColor(token), alphaValue) : lightValue
);

/**
 * Returns the DLS alert red while dark mode is active, otherwise the plain
 * `red` color keyword. Use it for alert and error text, which keeps its
 * hard-coded light color and only takes the DLS red token in dark mode.
 *
 * ```typescript
 *   import {dlsAlertColor} from '@ion/dls/src/theme/theme';
 *   ...
 *
 *   // the dark red token in dark mode, otherwise 'red' ...
 *   const style = {color: dlsAlertColor(isDark)};
 * ```
 *
 * @param isDark `true` when dark mode is active.
 * @returns      The dark DLS red color when `isDark`, otherwise `'red'`.
 */
export const dlsAlertColor = (isDark: boolean): string => dlsDarkColor(isDark, 'red', 'red');

/**
 * Returns the DLS grey3 color while dark mode is active, otherwise the mid
 * grey `rgba(130, 130, 130, 1)`. Use it for muted secondary text, which keeps
 * its hard-coded light color and only takes the DLS grey3 token in dark mode.
 *
 * ```typescript
 *   import {dlsMutedColor} from '@ion/dls/src/theme/theme';
 *   ...
 *
 *   // the grey3 token in dark mode, otherwise mid grey ...
 *   const style = {color: dlsMutedColor(isDark)};
 * ```
 *
 * @param isDark `true` when dark mode is active.
 * @returns      The dark DLS grey3 color when `isDark`, otherwise
 *               `'rgba(130, 130, 130, 1)'`.
 */
export const dlsMutedColor = (isDark: boolean): string => dlsDarkColor(isDark, 'grey3', 'rgba(130, 130, 130, 1)');

/**
 * Returns whether the given Material UI theme carries the dark palette, which
 * is the `isDark` input of the dark color helpers for code that is handed the
 * theme, such as a `makeStyles` callback, rather than reading the theme mode.
 *
 * ```typescript
 *   import {dlsDarkColor, isDarkPalette} from '@ion/dls/src/theme/theme';
 *   ...
 *
 *   const useStyles = makeStyles((theme: DLSTheme) => ({
 *     border : {
 *       // the dark grey5 token when the palette is dark ...
 *       color : dlsDarkColor(isDarkPalette(theme), 'grey5', '#E0E0E0')
 *     }
 *   }));
 * ```
 *
 * @param theme The Material UI theme, whose palette type is read.
 * @returns     `true` when the palette type of `theme` is `dark`, otherwise
 *              `false`.
 */
export const isDarkPalette = (theme: Pick<Theme, 'palette'>): boolean => (
  theme.palette.type === 'dark'
);

/**
 * Recolors an inline SVG (encoded as a Base64 string) by replacing an existing
 * `fill` color with a new one.
 *
 * This is useful when you have a static Base64-encoded SVG icon and want to
 * dynamically adjust its color to match DLS theme definitions.
 *
 * ```typescript
 *   // Example: replace #0097AF with grey3
 *   const updatedSvg = dlsSvgColorize(base64Icon, dlsColor('grey3'));
 *
 *   // Example: replace custom old color with caBlue
 *   const updatedSvg = dlsSvgColorize(base64Icon, dlsColor('caBlue'), '#FF0000');
 * ```
 *
 * @param base64Data - The Base64-encoded SVG string to modify.
 * @param newColor - The new color value to apply (hex or rgba).
 * @param oldColor - The original color value to replace. Defaults to `#0097AF`.
 * @returns A new Base64-encoded SVG string with the updated color.
 */
export const dlsSvgColorize = (
  base64Data: string,
  newColor: string,
  oldColor: string = '#0097AF'
) => btoa(atob(base64Data).replace(new RegExp(oldColor, 'gi'), newColor));

/**
 * @private
 *
 * Provides a helper function which iterates through a specific dls entry and
 * returns the value.
 */
const getEntry = (dls: any, ...args: string[]) => {
  let val: any;
  for (const arg of args) {
    val = (val || dls)[arg];
  }
  return val;
};

/**
 * @private
 */
const logger = (context, ...args: string[]) => {
  warn(
    `iOn DLS: Invalid DLS ${context} requested [${args.toString()}], please refer to the ${context} definitions defined in theme.json5.`
  );
};

export const getStyleClass = (name: string, classes: any, isCurrentTheme: boolean) => (
  isCurrentTheme ? classes[`current-${name}`] : classes[name]
);

/**
 * Creates the default Material UI DLS Theme used by all iOn Class of Applications.
 * As noted, by default, all iOn Applications are to use the default theme unless
 * white labeling is required, in which case, the createCustomTheme API is to be
 * used exclusively.
 *
 * The following outlines the process for using the default iOn DLS theme within
 * an application:
 *
 * ```typescript
 *   // src/pages/_app.tsx
 *
 *   // 1.) import default theme factory function ...
 *   import createDefaultTheme from '@ion/dls/src/theme/theme';
 *
 *   // 2.) create a reference to the default application theme ...
 *   const theme = createDefaultTheme();
 *
 *   // 3.) Set the theme in the application ThemeProvider ...
 *   function Application({...}: AppProps) {
 *     ...
 *     // set theme via createCustomTheme with application specific theme.json5
 *     return (
 *       <I18nextProvider i18n={i18n}>
 *         <ThemeProvider theme={theme}>
 *           <ErrorBoundary>
 *             <Component {...pageProps} />
 *           </ErrorBoundary>
 *         </ThemeProvider>
 *       </I18nextProvider>
 *     );
 *   };
 * ```
 * @see createDefaultTheme
 * @returns A Material UI Theme which implements the iOn DLS.
 */
const createDefaultTheme = (): DLSTheme => {
  const muiTheme = createTheme(getThemeConfig()) as DLSTheme;
  return muiTheme;
};

/**
 * Allows for providing an overridden theme for supporting application white
 * labeling. In general, applications should use the default theme via the
 * default export in order to ensure compatibility and strict adherence to
 * the CalAmp DLS. Applications can use the createCustomTheme API to allow for
 * white labeling as well as for theme switching if required.
 *
 * It is important to note that, as with any overridden values, custom themes
 * must have the exact same API (same property structure) as the default DLS
 * theme.json5 document. Additionally, project specific theme keys and values
 * can be defined if needed. This essentially allows for extended a theme as
 * well as inheriting from the default theme.
 *
 * The following outlines the typical usage for implementing a custom theme.
 *
 * First, define a custom `theme.json5` document, providing the given overrides
 * as needed:
 *
 * ```typescript
 *   // src/theme/current/theme.json5
 *   {
 *     dls: {
 *       // Defines all White Labeled Color overrides
 *       colors: {
 *        aaBlue : '#000000'
 *       }
 *       ... // define all other relevant overrides ...
 *     }
 *   }
 * ```
 *
 * Next, create the applications theme module and export the custom theme:
 *
 * ```typescript
 *   // src/theme/theme.ts
 *   import {createCustomTheme} from '@ion/dls/src/theme/theme';
 *   import json from './theme.json5';
 *
 *   export default createCustomTheme(json);
 * ```
 *
 * Then, apply the theme to the application:
 *
 * ```typescript
 *   // src/pages/_app.tsx
 *
 *   // import custom theme ...
 *   import theme from '../theme/theme';
 *
 *   // import white labelled font overrides (if needed, otherwise import DLS fonts)
 *   // note: if fonts will be white labeled, add them to the applications /public
 *   // directory, for example /public/assets/fonts
 *   import '../fonts/fonts.scss';
 *
 *   // if  white labelled font overrides are not needed import DLS fonts ...
 *   import '@ion/dls/src/fonts/fonts.scss';
 *
 *   // import shared application styles ...
 *   import '@ion/applications/src/styles/index.scss';
 *   ...
 *
 *   // set theme via createCustomTheme with application specific theme.json5
 *   return (
 *     <I18nextProvider i18n={i18n}>
 *       <ThemeProvider theme={theme}>
 *         <ErrorBoundary>
 *           <Component {...pageProps} />
 *         </ErrorBoundary>
 *       </ThemeProvider>
 *     </I18nextProvider>
 *   );
 * ```
 *
 * @param   overrides Defines the JSON5 document which implements the DLS theme
 *          JSON5 API.
 * @param   includeDefaults When true, indicates that the existing DLS theme.json5
 *          is to be merged with the override JSON5 document, allowing for providing
 *          a subset of overrides as needed. This parameter defaults to true as
 *          applications are encouraged to only provide specific overrides where
 *          required in order to reduce redundancy while also leveraging all new
 *          updates defined within the DLS.
 * @param   latest When true, the latest DLS theme will be used, otherwise, when
 *          `false` (default), the legacy DLS theme will be used.
 * @returns A Material UI Theme which implements the iOn DLS.
 */
export const createCustomTheme = (overrides: GenericObject, includeDefaults = true, latest = false): DLSTheme => {
  const theme = includeDefaults ? (
    merge(deepCopy(latest ? currentTheme : legacyTheme), overrides)
  ) : overrides;
  const muiTheme = createTheme(theme) as DLSTheme;
  customTheme = muiTheme;
  activeBaseTheme = latest ? DLSThemes.current : DLSThemes.legacy;
  dls     = muiTheme.dls;
  palette = muiTheme.palette;
  return muiTheme;
};

/**
 * The `createExternalTheme` API allows for retrieving an externally defined theme
 * and generating an applications theme based on the retrieved theme.json document.
 *
 * This API is similar to the `createCustomTheme` API in that it allows for creating
 * a custom theme and / or merging the default theme, with the main difference being
 * that the `createExternalTheme` API allows for retrieving the custom theme from an
 * external resource (URL).
 *
 * This API is primarily used for providing application white-labelling, whereas the
 * `createCustomTheme` API is generally used for **CalAmp** managed applications which
 * require overriding the default theme.
 *
 * ```typescript
 *   // src/pages/_app.tsx
 *   import {createExternalTheme} from '@ion/dls/src/theme/theme';
 *
 *   ...
 *
 *   // store a reference to the theme in state ...
 *   const [theme, setTheme] = useState<DLSTheme|null>(null);
 *
 *   // on mount retrieve and create the external theme ...
 *   useEffect(() => {
 *     (async() => {
 *       const theme = await createExternalTheme('/theme.json');
 *       setTheme(theme);
 *      })();
 *   }, [])
 *
 *   return (
 *     <I18nextProvider i18n={i18n}>
 *       <ThemeProvider theme={theme}>
 *         <ErrorBoundary>
 *           <Component {...pageProps} />
 *         </ErrorBoundary>
 *       </ThemeProvider>
 *     </I18nextProvider>
 *   );
 * ```
 * *Important Note*: While the above demonstrates how the `createExternalTheme` API
 * can be used directly, clients should generally use the `useExternalTheme` hook as
 * it not only encapsulates the above implementation, but provides additional utility
 * such as release retrieval caching, local persistence caching, and more.
 *
 * @param   url The url from which the external theme is to be loaded.
 * @param   includeDefaults When true, indicates that the existing DLS theme.json5
 *          is to be merged with the override JSON5 document, allowing for providing
 *          a subset of overrides as needed. This parameter defaults to true as
 *          applications are encouraged to only provide specific overrides where
 *          required in order to reduce redundancy while also leveraging all new
 *          updates defined within the DLS.
 * @param   latest When true, the latest DLS theme will be used, otherwise, when
 *          `false` (default), the legacy DLS theme will be used.
 * @returns A Material UI Theme which implements the iOn DLS.
 */
export const createExternalTheme = async(url: string, includeDefaults = true, latest = false): Promise<DLSTheme> => {
  const json = await loadExternalTheme(url);
  return createCustomTheme(json, includeDefaults, latest);
};

/**
 * Provides the default export, which returns a created instance of the Material UI
 * theme based on the pre-defined theme.json5 document DLS specifications.
 *
 * https://github.com/callemall/material-ui/blob/master/src/styles/getMuiTheme.js
 */
export default createDefaultTheme;
